import React from 'react';

import {
  CircleOutlined, Group, TaskAlt,
} from '@mui/icons-material';
import {
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';

import { Lesson } from '../../services/classes/types';
import { Course } from '../../services/courses/types';
import { ShortStaff, Staff } from '../../services/staff/types';
import { getLessonClassName, getLessonCourse } from '../../utils/common/classes';
import { getColorKeyFromCourse, getNameFromCourse } from '../../utils/common/courses';
import { joinNames } from '../../utils/common/formatText';
import Chip from '../Chip';
import ProfileIcon from '../ProfileIcon';

import {
  BottomContainer,
  LessonLabel,
  LessonNoteTypography,
  LessonTime,
  StatusContainer,
  StyledCard,
  StyledNoteIcon,
} from './index.styles';

interface LessonCardProps {
  lesson: Lesson;
  teacher?: ShortStaff | Staff;
  courses: Course[];
  newCount?: number;
  selected?: boolean,
  showFullDate?: boolean,
  hideStatusChips?: boolean,
  hideClassName?: boolean,
  hideTeacher?: boolean,
  hideProfileIcon?: boolean,
  onSelect?: () => void,
  greyedOut?: boolean,
}

const LessonCard = ({
  lesson,
  teacher,
  courses,
  newCount = 0,
  selected = false,
  hideStatusChips = false,
  showFullDate = false,
  hideClassName = false,
  hideTeacher = false,
  hideProfileIcon = false,
  onSelect = () => {},
  greyedOut = false,
}: LessonCardProps) => {
  const theme = useTheme();

  const {
    startDatetime,
    rollMarked,
    studentCount,
    absenteeCount,
    makeUpCount,
    onlineCount,
    notes,
    term,
    week,
  } = lesson;

  // Split course from lesson as the string before the first #
  const course = getLessonCourse(lesson);
  const lessonCode = lesson.lesson.split('#')[1];

  const className = getLessonClassName(lesson);
  const label = joinNames([getNameFromCourse(course, courses), lessonCode]);
  const color = getColorKeyFromCourse(course, courses) ?? 'primary';

  const teacherFirstName = teacher?.firstName ?? 'Unassigned';
  const teacherLastInitial = teacher?.lastName ? `${teacher?.lastName.charAt(0)}.` : '';

  return (
    <StyledCard elevation={0} $selected={selected} $greyedOut={greyedOut}>
      <CardActionArea onClick={onSelect}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item container xs={12} spacing={2}>
              {!hideProfileIcon && (
                <Grid
                  item
                  xs="auto"
                  container
                  justifyContent="center"
                  alignItems="center"
                >
                  <ProfileIcon
                    firstName={teacherFirstName}
                    lastName={teacherLastInitial}
                    color={teacher?.profileIcon?.color}
                    imageUrl={teacher?.profileIcon?.image}
                    size={64}
                  />
                </Grid>
              )}
              <Grid item xs="auto" container direction="column">
                {showFullDate && (
                  <Grid item>
                    <Typography color={theme.palette.gray.main}>
                      {`${dayjs(startDatetime).format('D MMM YYYY, ddd h:mm a')}${term !== undefined && week !== undefined ? ` (T${term}W${week})` : ''}`}
                    </Typography>
                  </Grid>
                ) }
                <Grid container spacing={3}>
                  {(!showFullDate || !hideTeacher) && (
                    <Grid
                      item
                      xs="auto"
                      direction="column"
                      container
                      justifyContent="center"
                    >
                      {!showFullDate &&
                      <LessonTime variant="h5">{dayjs(startDatetime).format('h:mm a')}</LessonTime>}
                      {!hideTeacher &&
                      <Typography>{joinNames([teacherFirstName, teacherLastInitial])}</Typography>}
                    </Grid>
                  )}
                  <Grid
                    item
                    xs="auto"
                    direction="column"
                    container
                    justifyContent="center"
                  >
                    <LessonLabel variant="h5" $color={color}>{label}</LessonLabel>
                    {!hideClassName && <Typography>{className}</Typography>}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item container xs justifyContent="flex-end">
                <Stack direction="row" spacing={theme.spacing(1)} color={theme.palette.gray.main}>
                  <Group />
                  <Typography>{studentCount}</Typography>
                </Stack>
              </Grid>
            </Grid>
            {(notes || notes.length > 0) && (
              <Grid item xs={12}>
                <LessonNoteTypography variant="body2"><StyledNoteIcon />{notes}</LessonNoteTypography>
              </Grid>
            )}
            {!hideStatusChips && (
              <Grid item xs={12}>
                <BottomContainer>
                  <StatusContainer>
                    <Chip
                      disabled={makeUpCount === 0}
                      color="lightOrange"
                      label={`${makeUpCount} Makeup`}
                      allCaps
                      cursorType="pointer"
                    />
                    <Chip
                      disabled={onlineCount === 0}
                      color="lightBlue"
                      label={`${onlineCount} Online`}
                      allCaps
                      cursorType="pointer"
                    />
                    <Chip
                      disabled={newCount === 0}
                      color="lightGreen"
                      label={`${newCount} New`}
                      allCaps
                      cursorType="pointer"
                    />
                    <Chip
                      disabled={absenteeCount === 0}
                      color="lightRed"
                      label={`${absenteeCount} Absent`}
                      allCaps
                      cursorType="pointer"
                    />
                  </StatusContainer>
                  <Chip
                    label="Roll"
                    variant={rollMarked ? 'filled' : 'outlined'}
                    color={rollMarked ? 'success' : 'error'}
                    allCaps
                    icon={rollMarked ? <TaskAlt /> : <CircleOutlined />}
                    cursorType="pointer"
                  />
                </BottomContainer>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </CardActionArea>
    </StyledCard>
  );
};
export default LessonCard;
